import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Package, ShoppingCart, Ticket, TrendingUp, Activity, BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const COLORS = ["#0033A0", "#DA291C", "#0084C7", "#F5B700", "#0F9B7F"];

const CATEGORY_LABEL = { laptop: "Laptops", phone: "Phones", tablet: "Tablets", monitor: "Monitors", accessory: "Accessories" };
const PERSONA_LABEL = { developer: "Developer", designer: "Designer", executive: "Executive", sales: "Sales", field_engineer: "Field Eng." };

function KpiCard({ label, value, icon: Icon, accent = false, testId }) {
  return (
    <Card className={`p-5 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : ""}`} data-testid={testId}>
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={16} />
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value ?? "—"}</div>
    </Card>
  );
}

export default function AdminReports() {
  const { persona } = usePersona();
  const { brand } = useBranding();
  const [data, setData] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personaOnly, setPersonaOnly] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      endpoints.reports(personaOnly ? persona.id : null),
      endpoints.fleetHealth(personaOnly ? persona.id : null),
    ])
      .then(([d, f]) => { setData(d); setFleet(f); setLoading(false); })
      .catch(() => setLoading(false));
  }, [personaOnly, persona.id]);

  if (loading || !data) {
    return <div className="grid grid-cols-4 gap-4"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;
  }

  const categoryData = data.category_breakdown.map((c) => ({ name: CATEGORY_LABEL[c.category] || c.category, value: c.count }));
  const personaData = data.persona_breakdown.map((p) => ({ name: PERSONA_LABEL[p.persona] || p.persona, count: p.count }));
  const statusData = data.status_breakdown.map((s) => ({ name: s.status, count: s.count }));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-reports-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0033A0] grid place-items-center">
              <BarChart3 size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{brand.small} · Flexspace · Admin</div>
              <div className="font-display font-bold text-slate-900 text-lg leading-tight">Reports · Fleet Analytics</div>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">Admin view · Fleet-wide</Badge>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-8 py-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Enterprise reports</div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">Flexspace Insights</h1>
          <p className="text-sm text-slate-600 mt-1">Enterprise-wide device ordering and fulfillment analytics.</p>
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-4 py-2 shadow-sm">
          <Switch id="persona-scope" checked={personaOnly} onCheckedChange={setPersonaOnly} data-testid="reports-scope-toggle" />
          <Label htmlFor="persona-scope" className="text-sm text-slate-700 cursor-pointer">Scope: {personaOnly ? persona.name : "All personas"}</Label>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Pending Orders" value={data.kpis.pending_orders} icon={ShoppingCart} accent testId="report-kpi-pending" />
        <KpiCard label="Devices in Use" value={data.kpis.devices_in_use} icon={Package} testId="report-kpi-devices" />
        <KpiCard label="Open Tickets" value={data.kpis.open_tickets} icon={Ticket} testId="report-kpi-tickets" />
        <KpiCard label="Total Orders" value={data.kpis.total_orders} icon={TrendingUp} testId="report-kpi-total" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200 shadow-sm" data-testid="chart-persona">
          <h3 className="font-display font-bold text-lg text-slate-900">Orders by Persona</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={personaData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="count" fill="#0033A0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm" data-testid="chart-category">
          <h3 className="font-display font-bold text-lg text-slate-900">Device Category Mix</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm" data-testid="chart-trend">
          <h3 className="font-display font-bold text-lg text-slate-900">Monthly Order Trend</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                <Line type="monotone" dataKey="count" stroke="#DA291C" strokeWidth={2.5} dot={{ r: 4, fill: "#DA291C" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm" data-testid="chart-status">
          <h3 className="font-display font-bold text-lg text-slate-900">Order Status Breakdown</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} width={90} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="count" fill="#0033A0" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Fleet Health Section (Nexthink DEX) */}
      {fleet && (
        <Card className="p-6 border-slate-200 shadow-sm" data-testid="fleet-health">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <Activity size={12} className="text-[#0033A0]" /> Nexthink · Fleet Health
              </div>
              <h3 className="font-display font-bold text-xl text-slate-900 mt-1">Digital Employee Experience</h3>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fleet Avg Device UX</div>
              <div className="font-display font-black text-4xl text-slate-900" data-testid="fleet-avg-dex">{fleet.avg_dex}</div>
              <div className="text-xs text-slate-500">{fleet.device_count} devices</div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3 mt-5">
            <div className="border border-slate-200 rounded-md p-3 text-center bg-green-50">
              <div className="text-[10px] uppercase tracking-widest text-green-700 font-semibold">Excellent (≥85)</div>
              <div className="font-display font-black text-3xl text-slate-900 mt-1" data-testid="fleet-excellent">{fleet.distribution.excellent}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3 text-center bg-amber-50">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Good (70-84)</div>
              <div className="font-display font-black text-3xl text-slate-900 mt-1" data-testid="fleet-good">{fleet.distribution.good}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3 text-center bg-red-50">
              <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Poor (&lt;70)</div>
              <div className="font-display font-black text-3xl text-slate-900 mt-1" data-testid="fleet-poor">{fleet.distribution.poor}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Device UX by Persona</div>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fleet.by_persona.map((p) => ({ name: PERSONA_LABEL[p.persona] || p.persona, avg: p.avg }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#475569" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                    <Bar dataKey="avg" fill="#0033A0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Top Issues</div>
              <ul className="space-y-2" data-testid="fleet-top-issues">
                {fleet.top_issues.map((i) => (
                  <li key={i.label} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2">
                    <span className="text-sm text-slate-700">{i.label}</span>
                    <span className="text-xs font-semibold bg-red-100 text-red-800 px-2 py-0.5 rounded">{i.count} devices</span>
                  </li>
                ))}
                {fleet.top_issues.length === 0 && (
                  <li className="text-xs text-slate-500 text-center py-4">No systemic issues detected 🎉</li>
                )}
              </ul>
            </div>
          </div>
        </Card>
      )}
      </div>
    </div>
  );
}
